import { useEffect } from 'react';
import { Configure } from 'react-instantsearch';
import Head from 'next/head';
import { capitalize } from 'lodash-es';

import { DynamicComponents, Error, FlatSheet } from '@/templates';

import { InstantSearch } from '@/components/algolia';
import { flatName, flatPath, zonePath } from '@/components/algolia/utils';
import { getPage } from '@/graphql/queries/getPage';
import { getProperties } from '@/graphql/queries/getProperties';
import { getProperty } from '@/graphql/queries/getProperty';
import { routes } from '@/lib';
import { generatedListingConfig } from '@/lib/contexts/listing-config.generated';
import { flatPropertiesTransformer, gtmPush } from '@/utils';

import type { Apartment, SingleFamilyResidence, WithContext } from 'schema-dts';

const hasValidPrice = (p: any): boolean => {
  const price =
    p?.ventaAlquiler?.toUpperCase() === 'VENTA'
      ? p?.importeVenta
      : p?.importeRecibofinal;
  return price != null && price !== '' && parseFloat(price) > 0;
};

const FlatDetail = ({ errorCode, page, property, search }: any) => {
  useEffect(() => {
    gtmPush({
      event: 'view_item',
      currency: 'EUR',
      item_type: capitalize(property?.ventaAlquiler),
      items: [
        {
          item_id: property?.codigoUnidadAlquiler,
          item_name: property?.nombreUE,
          currency: 'EUR',
          discount: '',
          item_list_id: zonePath(property, property?.ventaAlquiler),
          item_list_name: `${property?.provincia} > ${property?.ciudad}${
            property?.tipoZona?.infoAdicional
              ? ` > ${property.tipoZona.infoAdicional}`
              : ''
          }`.toLowerCase(),
          price: parseFloat(
            property?.ventaAlquiler?.toLowerCase() === 'venta'
              ? property?.importeVenta
              : property?.importeRecibofinal,
          ),
          city: property?.ciudad,
          city_area: property?.tipoZona?.infoAdicional,
          street: property?.STREET,
          address: property?.STREET,
          property_type: property?.tipoDeUsoDescripcion,
          property_condition: property?.estadoDescripcion,
          area: property?.metrosComputablesUA ?? undefined,
          bedrooms_quantity: property?.habitaciones,
          bathrooms_quantity: property?.banos,
          elevator: !!property?.Ascensores,
          communal_expenses: undefined,
          parking: property?.hasGaraje, //
          furnishings: property?.hasAmueblado, //
          floor: undefined,
          heating: property?.instalacionCaldera,
        },
      ],
    });
  });

  const commonSchema: any = {
    name: property?.nombreUE,
    description: property?.otrosDatos?.argumentario,
    numberOfRooms: parseInt(property?.habitaciones) + parseInt(property?.banos),
    numberOfBathroomsTotal: property?.banos,
    numberOfBedrooms: property?.habitaciones,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ES',
      addressLocality: property?.provincia,
      addressRegion: property?.ciudad,
      streetAddress: property?.nombreUE,
    },
    latitude: property?.latitude,
    longitude: property?.longitude,
  };

  const apartmentSchema: WithContext<Apartment> = {
    '@context': 'https://schema.org',
    '@type': 'Apartment',
    ...commonSchema,
  };

  const familyResidenceSchema: WithContext<SingleFamilyResidence> = {
    '@context': 'https://schema.org',
    '@type': 'SingleFamilyResidence',
    ...commonSchema,
  };

  // El CTA hipoteca, si existe en el contenido de /vivienda, se saca del flujo
  // posterior y se renderiza entre la descripción y las características (orden Figma HoM).
  const content: any[] = page?.attributes?.Content ?? [];
  const ctaHipotecaBlock = content.find(
    (c) => c?.__typename === 'ComponentRevisadasCtaHipoteca',
  );
  const beforeFeatures = ctaHipotecaBlock ? (
    <DynamicComponents
      type={ctaHipotecaBlock.__typename}
      {...ctaHipotecaBlock}
      noContainer
      propertyId={property?.codigoUnidadAlquiler}
    />
  ) : null;

  return errorCode === 404 ? (
    <Error statusCode={errorCode} />
  ) : (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              property?.ventaAlquiler === 'COMPRA'
                ? familyResidenceSchema
                : apartmentSchema,
            ),
          }}
        />
      </Head>

      <FlatSheet
        property={property}
        beforeFeatures={beforeFeatures}
        contactEyebrow={page?.attributes?.contactoSidebarEyebrow}
        contactTitle={page?.attributes?.contactoSidebarTitulo}
      />

      <InstantSearch
        indexName={
          process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? ''
        }
      >
        {/* +1 sobre el max de `Columnas` en ViviendasMasBuscadas (4) para
            absorber el filtro de "quita el flat actual" que hace el bloque. */}
        <Configure aroundLatLng={search} hitsPerPage={5} />
        {content
          .filter((c) => c?.__typename !== 'ComponentRevisadasCtaHipoteca')
          .map(({ __typename, ...props }: any, index: number) => (
            <DynamicComponents
              type={__typename}
              {...props}
              key={`${__typename}-${index}`}
              propertyId={property?.codigoUnidadAlquiler}
              precioInmueble={property?.importeVenta}
            />
          ))}
      </InstantSearch>
    </>
  );
};

export async function getStaticProps({ params, preview = false }: any) {
  const { pages, menus, preFooter, hasCustomFooter, footerStyle, stickyMenu } =
    await getPage('/vivienda', preview);
  const pathSplit =
    params?.id?.[0]?.split(routes.flats.flat('')).filter(Boolean)[0] ?? '';
  const id = pathSplit.match(/\d{4}-\d{8}-\d{8}/)?.[0].replaceAll('-', '/');
  const { zfmDisponiblidads } = await getProperty(id);
  const property = zfmDisponiblidads?.[0] || {};
  // SAP publishes units before their price is loaded. They stay 404 until
  // then; `revalidate` picks them up once the price arrives.
  const notFound = zfmDisponiblidads?.length == 0 || !hasValidPrice(property);
  const search = `${property?.latitud || 0},${property?.longitud || 0}`;

  if (property?.caracteristicas)
    property.caracteristicas = flatPropertiesTransformer(
      property.caracteristicas,
    );

  return {
    props: {
      page: {
        title: flatName(zfmDisponiblidads?.[0]),
        metaDescription: flatName(zfmDisponiblidads?.[0]),
        metaKeywords: null,
        metaRobots: '',
        path: flatPath(zfmDisponiblidads?.[0]),
        theme: 'light',
        langcode: { id: 'es' },
        attributes: pages?.[0] || null,
        menu: menus || [],
        preFooter: preFooter || null,
        hasCustomFooter,
        footerStyle,
        stickyMenu,
        transparentMenu: generatedListingConfig.viviendaPageStyle === 'hero',
        preview,
      },
      search,
      property,
    },
    revalidate: process.env.NEXT_PUBLIC_REVALIDATE
      ? parseInt(process.env.NEXT_PUBLIC_REVALIDATE)
      : 1000,
    notFound,
  };
}

export async function getStaticPaths() {
  const { zfmDisponiblidads } = await getProperties();

  return {
    paths: zfmDisponiblidads
      .filter(hasValidPrice)
      .map((itm: any) => flatPath(itm)),
    fallback: 'blocking',
  };
}

export default FlatDetail;
